import React from "react";
import { withStyles } from "@material-ui/core";
import { compose } from "recompose";
import { withUserProfile } from "../../../../../userProfile/userProfileContext";
import SearchBox from "./Search";
import Grid from "@material-ui/core/Grid";
import DriverPerformanceFilters from "./Filters";
import DriverPerformanceLegend from "./DriverPerformanceLegend";
import MaximiseIcon from "@material-ui/icons/Fullscreen";
import CircularProgress from "@material-ui/core/CircularProgress";
import { withSnackbar } from "notistack";
import DispatchGantt from "../Gantt/DispatchGantt";
import ArrowBackIcon from "@material-ui/icons/KeyboardArrowLeft";
import Message from "../../../Dispatch/RouteExecution/Drivers/DriverInfoPopup/components/Message";
import MessageHistory from "../../../Dispatch/DispatchChat/MessageHistory";
import { getDefaultDateRange } from "../../../Dispatch/DispatchChat/MessageHistory/utils";
import { getAllDriversForMessageHistory } from "../util";
import PrintModal from "./Print/PrintModalGrid";
import Events from '../../../../../utilities/EventEmitter';

const styles = (theme) => ({
  headerStyles: {
    display: "flex",
    alignItems: "center",
    padding: 4,
    borderBottom: `1px solid ${theme.AppBGColor}`,
    paddingTop: 0,
  },
  headerContentContainer: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    margin: "0 10 0 20",
  },
  headerLeft: {
    justifyContent: "flex-start",
  },
  fullScreenIcon: {
    fontSize: 24,
    marginRight: 12,
    color: theme.iconColor,
    cursor: "pointer",
  },
  reportContainer: {
    height: "calc(100% - 185px)",
  },
  moduleHeader: {
    color: theme.HeaderSelectedText,
    borderBottom: `1px solid ${theme.AppBGColor}`,
    fontWeight: "bold",
    padding: `10px 32px`,
    fontSize: 14,
  },
  reportsGridHeader: {
    padding: "10px 20px",
    borderBottom: "0px",
    justifyContent: "space-between",
    width: "100%",
    display: "flex",
  },
  root: {
    flexGrow: 1,
  },
  filterContainer: {
    display: "flex",
    justifyContent: "space-evenly",
  },
  footerContainer: {
    borderTop: `1px solid ${theme.AppBGColor}`,
    margin: 3,
  },
  footerDetails: {
    lineHeight: 1,
    fontWeight: 600,
    fontSize: 12,
    color: theme.HeaderSelectedText,
  },
  reportControlsContainer: {
    marginLeft: 8,
    marginRight: 16,
    marginTop: 8,
  },
  searchContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    justifyContent: "space-between",
    padding: 8,
    maxWidth: "90%",
  },
  calendarIcon: {
    color: theme.HeaderSelectedText,
  },
  ganttContainer: { marginTop: -8, paddingLeft: 8, paddingRight: 8 },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
  noDataText: {
    fontSize: "16px",
    lineHeight: "22px",
    color: theme.iconColor,
  },
  backButton: {
    color: theme.gridTextColor,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    fontSize: theme.headerTextFontSize,
    fontWeight: 600,
  },
  dialog: {
    padding: 0,
    boxSizing: 'border-box',
    borderRadius: '2px',
    backgroundColor: theme.ModalBGColor,
    boxShadow: '2px 2px 4px black',
    minWidth: '30%',
    maxWidth: '30%',
    height: '40%'
  }
});

class DriverPerformanceRenderer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      searchText: '',
      isReportExpanded: false,
      openMessageHistory: false,
      showPrintDialog: false
    };
  }

  componentDidMount() {
    Events.on('openPrintDialog', this.openPrintDialog);
  }

  componentWillUnmount() {
    Events.off('openPrintDialog', this.openPrintDialog);
  }

  openPrintDialog = (dispatchData) => {
    this.setState({
      showPrintDialog: true,
      dispatchData
    });
  };

  closePrintDialog = () => {
    this.setState({
      showPrintDialog: false,
      dispatchData: []
    });
  };

  toggleFullscreen = () => {
    this.setState((prevState) => ({
      isReportExpanded: !prevState.isReportExpanded
    }));
  };

  updateQuickFilterText = (event) => {
    this.setState({
      searchText: event.target.value
    });
  };

  onMessageHistoryClick = () => {
    this.setState({
      openMessageHistory: true
    });
  }

  closeMessageHistoryClick = () => {
    this.setState({
      openMessageHistory: false
    });
  }

  render() {
    const { classes, showBackButton, userData, startDate, endDate, selectedFilterData = {} } =
      this.props;
    const {
      code: currentTerminalCode,
      offset: currentTerminalOffset,
      id: terminalId
    } = userData.userProfile.terminal;
    return (
      <>
        <div className={classes.headerStyles}>
          {showBackButton && (
            <div className={classes.backButton} onClick={this.props.goBack}>
              <ArrowBackIcon classes={{ root: classes.backIcon }} /> Back
            </div>
          )}
          <header className={`${classes.moduleHeader} ${classes.reportsGridHeader}`}>
            <div
              className={`${classes.headerContentContainer} ${
                !showBackButton ? classes.headerLeft : ''
              }`}>
              <span>Driver Performance</span>
              {showBackButton && (
                <Message
                  onClick={this.onMessageHistoryClick}
                  disabled={!this.props.data?.length}
                  disabledTooltipMessage="No drivers found."
                  tooltipMessage="View message history"
                />
              )}
            </div>
            {this.props.data && (
              <span onClick={this.toggleFullscreen}>
                <MaximiseIcon className={classes.fullScreenIcon} />
              </span>
            )}
          </header>
        </div>
        <div className={classes.root}>
          <Grid container className={classes.reportContainer} spacing={16}>
            <Grid item xs={12} className={classes.reportControlsContainer}>
              <Grid container spacing={16}>
                <Grid className={classes.filterContainer} item xs={7}>
                  <DriverPerformanceFilters
                    data={this.props.data}
                    updateFilteredData={this.props.updateFilteredData}
                    isDataRefreshed={this.props.isDataRefreshed}
                    terminalId={terminalId}
                    dispatchDate={this.props.currentDispatchDate}
                    hideViolationsFilter={this.props.hideViolationsFilter}
                    hideDriversFilter={this.props.hideDriversFilter}
                  />
                </Grid>
                <Grid item xs={5}>
                  <div className={classes.searchContainer}>
                    <SearchBox
                      searchText={this.state.searchText}
                      updateQuickFilterText={this.updateQuickFilterText}
                    />
                    <DriverPerformanceLegend />
                  </div>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              {!this.props.isLoading && this.props.data && (
                <div className={classes.ganttContainer}>
                  <DispatchGantt
                    searchText={this.state.searchText}
                    tasks={this.props.formattedFilteredData}
                    showCurrentMarker={Boolean(this.props.isRealtimeReport)}
                    terminalOffset={currentTerminalOffset}
                    isReadOnly
                    isFullScreen={this.state.isReportExpanded}
                    showFullscreenControl
                    isRealtimeReport={this.props.isRealtimeReport}
                    startDate={startDate}
                    endDate={endDate}
                  />
                </div>
              )}
              {!this.props.data && (
                <div className={classes.loaderContainer}>
                  {this.props.isLoading && <CircularProgress size={40} />}
                  {!this.props.isLoading && (
                    <div className={classes.noDataText}>No data available</div>
                  )}
                </div>
              )}
            </Grid>
          </Grid>
        </div>
        {this.state.openMessageHistory && (
          <MessageHistory
            onClose={this.closeMessageHistoryClick}
            defaultDriver={this.props.data?.[0]?.driverId}
            defaultDateRange={getDefaultDateRange(
              selectedFilterData?.startDate ?? startDate,
              selectedFilterData?.endDate ?? endDate
            )}
            defaultTerminalCode={selectedFilterData.terminalCode ?? currentTerminalCode}
            defaultTerminalOffset={currentTerminalOffset}
            driverOptions={getAllDriversForMessageHistory(this.props.data)}
            isDriverChangeDisabled={!selectedFilterData.terminalCode}
          />
        )}
        {this.state.showPrintDialog && (
          <PrintModal
            open
            title="Print Manifest"
            handleClose={this.closePrintDialog}
            dialogClasses={classes.dialog}
            dispatchData={this.state.dispatchData}
          />
        )}
      </>
    );
  }
}
export default compose(
  withStyles(styles),
  withUserProfile,
  withSnackbar
)(DriverPerformanceRenderer);
